/**
 * Adattatore runtime del registro prodotti: legge ambiente, host e variabili di build
 * una sola volta all'avvio. Tutto il resto dell'app usa `platform` (o useProduct()).
 */
import {
  mountedProducts,
  productOrigin,
  resolveHost,
  type HostResolution,
  type PlatformEnv,
  type SuiteMode,
} from "./hosts";
import type { ProductKey } from "./products";

const LIVE_PROJECT_ID = "mhpwveugbtmaqvrmtzhg";
const OVERRIDE_STORAGE_KEY = "ml_product_override";

function detectEnv(): PlatformEnv {
  const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  if (projectId === LIVE_PROJECT_ID) return "live";
  if (projectId === "local") return "locale";
  return "collaudo";
}

/** ?product=construction (solo fuori da live) — resta valido per la sessione della scheda. */
function readOverride(env: PlatformEnv): string | null {
  if (env === "live" || typeof window === "undefined") return null;
  try {
    const fromQuery = new URLSearchParams(window.location.search).get("product");
    if (fromQuery === "suite") {
      sessionStorage.removeItem(OVERRIDE_STORAGE_KEY);
      return null;
    }
    if (fromQuery) sessionStorage.setItem(OVERRIDE_STORAGE_KEY, fromQuery);
    return sessionStorage.getItem(OVERRIDE_STORAGE_KEY);
  } catch {
    return null;
  }
}

const env = detectEnv();
const suiteMode: SuiteMode = import.meta.env.VITE_SUITE_MODE === "console" ? "console" : "legacy";

const resolution: HostResolution = resolveHost({
  hostname: typeof window !== "undefined" ? window.location.hostname : "",
  env,
  suiteMode,
  buildProduct: import.meta.env.VITE_PRODUCT ?? null,
  override: readOverride(env),
});

/**
 * Su live i domini dei prodotti si considerano attivi solo dopo il passaggio
 * (VITE_SUITE_MODE=console) o quando si sta già usando un dominio di prodotto:
 * prima di allora nessun link deve puntare a un dominio non ancora configurato.
 */
const domainsActive = env !== "live" || suiteMode === "console" || resolution.mode === "product";
/** In locale si resta sulla porta del dev server corrente (8080, 8082, …). */
const localPort = typeof window !== "undefined" ? window.location.port : "";
const originOf = (product: ProductKey): string | null =>
  domainsActive ? productOrigin(product, env, localPort) : null;

export const platform = {
  env,
  suiteMode,
  resolution,
  localPort,
  /** Prodotti montati su questo host */
  mounted: mountedProducts(resolution),
  /** Prodotto dedicato di questo host, o null in modalità suite */
  product: resolution.mode === "product" ? resolution.product : null,
  isMounted(product: ProductKey): boolean {
    return this.mounted.includes(product);
  },
  /**
   * URL assoluto di una pagina di un altro prodotto. Se il prodotto non ha un dominio
   * dedicato in questo ambiente (o è montato qui), resta un percorso relativo.
   */
  urlFor(product: ProductKey, path: string): string {
    if (this.isMounted(product)) return path;
    const origin = originOf(product);
    return origin ? `${origin}${path}` : path;
  },
  /**
   * Origine da usare nei link che escono dall'app (email, funnel onboarding, ritorni di pagamento)
   * per un prodotto: il suo dominio se ne ha uno, altrimenti l'origine corrente.
   */
  originFor(product: ProductKey): string {
    return originOf(product) ?? (typeof window !== "undefined" ? window.location.origin : "");
  },
};
